/**
 * Record handle — the ergonomic consumer surface. `auger.record(id)` returns a
 * lightweight, lazy handle (no fetch) whose relation families each expose
 * add/remove/set/clear. Every verb is pure sugar: it builds a `records.update`
 * changes object and calls the one engine. The family only types the target
 * (a link takes an id, a contact/time takes its value row); runtime routing is by
 * the field's real relationship inside the planner.
 */

import { fetchRecordContacts } from "../gql/contacts.js"
import type { Query } from "../index.js"
import type { createRecordsService, FullRecord } from "./records.js"
import type { WrittenRows } from "./relations.js"
import type { createSourcesService } from "./sources.js"

/** What every write returns: the re-read record (FullRecord), plus a row for every relation
 *  target this call added, keyed by relation and then by field key
 *  (`written.links.related_to`) — inserted, or the row that
 *  already existed (adding something that already exists returns the thing
 *  that already exists). `written` is how a caller names the row for what it
 *  added without re-reading and searching for it by value. */
export type UpdateResult = { record: FullRecord; written: WrittenRows }
/** What `add` and `remove` return: the rows they wrote, with no re-read. The
 *  re-read ran AFTER the commit, so a failed read turned a saved row into a
 *  reported failure, and the caller's retry wrote it twice. */
export type WriteResult = { written: WrittenRows }
type Update = ReturnType<typeof createRecordsService>["update"]

/** A link target: a record id, or an entity carrying one. */
export type LinkTarget = number | string | { id: number | string }
/** A contact value row. `id` is optional on the *shape*, but for `set()` it is
 *  effectively required per-target to keep that row — see `RelationApi.set`. */
export interface ContactTarget {
  id?: number | string
  contactType: string
  value: string
  label?: string
  [k: string]: unknown
}
/** A time value row. `id` is optional on the *shape*, but for `set()` it is
 *  effectively required per-target to keep that row — see `RelationApi.set`. */
export interface TimeTarget {
  id?: number | string
  timeType: string
  startAt: string
  endAt?: string
  [k: string]: unknown
}
/** A value-relation row (contact/time), addressed by its own row id — the id
 *  `computeRelationOps` actually matches `remove` targets against. Removing by
 *  the full value shape (`ContactTarget`/`TimeTarget`) can't work: those carry no
 *  `id`, so nothing in the current set would ever match. */
export type RowTarget = number | string | { id: number | string }

/** The add/remove/set/clear verbs for one relation family on a record, each
 *  compiled to a `records.update` changes object. `TRemove` defaults to `T` (a
 *  link removes by the same target shape it adds) but a value relation
 *  (contacts/times) narrows it to `RowTarget`, since removal is by row id. */
export interface RelationApi<T, TRemove = T> {
  /** Reconcile the field to exactly these targets. For a value relation
   *  (contacts/times), a SET never edits a row in place — it only adds and
   *  removes — so each target should carry its row `id` to keep that row;
   *  an id-less target is always an add, never a match against an existing
   *  row's content. */
  set(fieldKey: string, targets: T[]): Promise<UpdateResult>
  /** Add targets without touching the rest. Adding a target the field already
   *  holds inserts nothing and returns the existing row in `written`. */
  add(fieldKey: string, targets: T | T[]): Promise<WriteResult>
  /** Remove targets (by current value: linked id for links, row id for value rows). */
  remove(fieldKey: string, targets: TRemove | TRemove[]): Promise<WriteResult>
  /** Remove every target on the field. */
  clear(fieldKey: string): Promise<UpdateResult>
}

function relationApi<T, TRemove = T>(update: Update, id: number): RelationApi<T, TRemove> {
  const list = <U>(t: U | U[]): unknown[] => (Array.isArray(t) ? t : [t])
  return {
    set: (fieldKey, targets) => update(id, { [fieldKey]: targets }),
    add: (fieldKey, targets) => update(id, { [fieldKey]: { add: list(targets) } }, { read: false }),
    remove: (fieldKey, targets) => update(id, { [fieldKey]: { remove: list(targets) } }, { read: false }),
    clear: (fieldKey) => update(id, { [fieldKey]: [] }),
  }
}

export function createRecordHandle(
  records: ReturnType<typeof createRecordsService>,
  sources: ReturnType<typeof createSourcesService>,
  query: Query,
  id: number
) {
  return {
    get: () => records.get(id),
    update: (changes: Record<string, unknown>) => records.update(id, changes),
    destroy: () => records.destroy(id),

    links: relationApi<LinkTarget>(records.update, id),
    contacts: {
      ...relationApi<ContactTarget, RowTarget>(records.update, id),
      /** The record's contacts, primary first, then by position, then oldest first. */
      list: () => query(fetchRecordContacts, { recordId: id }).then((d) => d.contacts),
    },
    times: relationApi<TimeTarget, RowTarget>(records.update, id),

    // Sources are identified by (sourceType, sourceId), not a row id, so they
    // don't share the planner path — a shaped accessor instead of relationApi.
    sources: {
      list: () => sources.list(id),
      remove: (sourceType: string, sourceId: string) => sources.remove(id, sourceType, sourceId),
    },
  }
}
